import { RefObject, useEffect, useRef } from "react";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { GetRowCapabilities } from "../editorCapabilities";
import { EditorFocusTarget } from "../editorBodyTypes";
import { useEditorCellFocus } from "../useEditorCellFocus";
import { NestedEditorRow } from "./nestedRowTypes";
import { orRowCapabilities } from "./nestedRowConstituents";
import { scrollNestedRowIntoView } from "./scrollNestedRowIntoView";

interface UseNestedEditorFocusTargetParams {
    // The row a chart-view Edit came from; null for the toolbar Edit COA button.
    focusTarget: EditorFocusTarget | null | undefined;
    // Maps a constituent draft id (account seed / group id) to its display-key
    // row, the same id space the target carries.
    rowByConstituentId: ReadonlyMap<string, NestedEditorRow>;
    tableRef: RefObject<ViewTableHandle | null>;
    getRowCapabilities: GetRowCapabilities;
    // So the page can clear the target; the ref stops a tree change from
    // re-requesting focus before that clear lands.
    onFocusTargetConsumed?: () => void;
}

// Consumes the chart-view Edit focus target once per drawer open: resolves the
// constituent id to its display-key row, scrolls to it, and opens the name field
// when the row is inline-renameable. An unresolvable target does nothing (the
// drawer is already open). No expand is needed here: the drawer remounts the
// editor on every open, and `autoExpandOnMount` opens the whole tree, so the
// target's ancestors are already expanding. Scrolling is a no-op until the row
// joins the rendered model, which is exactly the wait we want.
export const useNestedEditorFocusTarget = ({
    focusTarget,
    rowByConstituentId,
    tableRef,
    getRowCapabilities,
    onFocusTargetConsumed,
}: UseNestedEditorFocusTargetParams): void => {
    const hasConsumedRef = useRef(false);
    const { requestFocus } = useEditorCellFocus(
        ({ rowId }) => scrollNestedRowIntoView(tableRef, rowId, "center"),
        (draftId) => rowByConstituentId.get(draftId)?.id ?? draftId,
    );

    useEffect(() => {
        if (hasConsumedRef.current || !focusTarget) {
            return;
        }
        hasConsumedRef.current = true;
        onFocusTargetConsumed?.();
        const row = rowByConstituentId.get(focusTarget.id);
        if (row === undefined) {
            return;
        }
        scrollNestedRowIntoView(tableRef, row.id, "center");
        const capabilities = orRowCapabilities(row, getRowCapabilities);
        // Group rows are always inline-renameable; account rows only when the
        // host does not lock rename and the cell is not a placeholder (same
        // predicate as NestedNameCell's `isRenameEditable`, ignoring the
        // session-wide editing lock which already blocks the whole editor).
        const isNameEditable =
            focusTarget.kind === "group" ||
            (capabilities.renameBlockReason == null &&
                capabilities.placeholder == null);
        if (isNameEditable) {
            requestFocus({ rowId: row.id, field: "name" });
        }
    }, [
        focusTarget,
        rowByConstituentId,
        getRowCapabilities,
        requestFocus,
        tableRef,
        onFocusTargetConsumed,
    ]);
};
